// ═══════════════════════════════════════════════════════════════════════
// ProjectHealthDashboardPage — Sprint 3 #2
// ═══════════════════════════════════════════════════════════════════════
// Cross-project rollup that surfaces every project's budget health in
// one screen. Three stacked panels:
//
//   1. Health summary tiles (5 status bands + total)
//   2. Filters (FY · Branch · Division · Project Status)
//   3. Project Health Grid — Budget / Consumed / Open / Revenue /
//      Current Profit / Expected Profit / Utilization % / Status
//   4. Net Commitment & Transfer Map — aggregated MTV + cross-project
//      IV value flows between projects (top edges + per-node totals)
//
// Backend wiring:
//   - GET /api/dashboard/budget-breach-alerts?fy_code=&branch_id=&division=&project_status=
//   - GET /api/dashboard/transfer-map?fy_code=&branch_id=&division=
//   - GET /api/projects/divisions
//   - GET /api/branches
//   - GET /api/financial-years

import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormTab } from "@/hooks/useFormTab";
import { api, formatINR } from "@/lib/api";
import {
  FileBarChart2, Activity, AlertTriangle, AlertOctagon, CheckCircle2,
  CircleSlash, Filter, ArrowRight, RefreshCw, ExternalLink, Search,
} from "lucide-react";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { ProjectCostSummaryTab } from "@/pages/Projects";

const STATUS_META = {
  Breach:         { label: "Budget Breach",          tone: "rose-bold",
                    icon: AlertOctagon,  hint: "> 100% of Budget" },
  Critical:       { label: "Critical",               tone: "rose",
                    icon: AlertTriangle, hint: "90% – 100% of Budget" },
  Warning:        { label: "Warning",                tone: "amber",
                    icon: AlertTriangle, hint: "80% – 90% of Budget" },
  Healthy:        { label: "Healthy",                tone: "emerald",
                    icon: CheckCircle2,  hint: "< 80% of Budget" },
  Not_Configured: { label: "Budget Not Configured",  tone: "slate",
                    icon: CircleSlash,   hint: "No budget set" },
};
const STATUS_ORDER = ["Breach", "Critical", "Warning", "Healthy", "Not_Configured"];
const STATUS_TONES = {
  "rose-bold": { tile: "bg-rose-100  text-rose-900  ring-rose-300",
                 chip: "bg-rose-200  text-rose-900",
                 row:  "bg-rose-50/70" },
  "rose":      { tile: "bg-rose-50   text-rose-800  ring-rose-200",
                 chip: "bg-rose-100  text-rose-800",
                 row:  "bg-rose-50/40" },
  "amber":     { tile: "bg-amber-50  text-amber-800 ring-amber-200",
                 chip: "bg-amber-100 text-amber-800",
                 row:  "bg-amber-50/40" },
  "emerald":   { tile: "bg-emerald-50 text-emerald-800 ring-emerald-200",
                 chip: "bg-emerald-100 text-emerald-800",
                 row:  "" },
  "slate":     { tile: "bg-slate-50   text-slate-700  ring-slate-200",
                 chip: "bg-slate-100   text-slate-700",
                 row:  "" },
};

const PROJECT_STATUS_OPTIONS = ["Active", "On Hold", "Completed", "Cancelled"];

export default function ProjectHealthDashboardPage() {
  const openFormTab = useFormTab();
  // Drill-down helper — opens the per-project Cost Summary as a DYNAMIC tab
  // so the inline render fires (the URL `/projects/cost-summary/<id>` is not
  // in TAB_REGISTRY, so a bare `openTab` call would render a blank screen).
  const openCostSummary = useCallback((row) => {
    const project = {
      id: row.project_id,
      project_code: row.project_code || "",
      project_name: row.project_name || "",
      branch_name: row.branch_name || "",
    };
    openFormTab({
      path: `/projects/cost-summary/${row.project_id}`,
      label: `Cost · ${project.project_code || project.project_name || "Project"}`,
      parent: "/project-health",
      render: ({ close }) => <ProjectCostSummaryTab project={project} onClose={close} />,
    });
  }, [openFormTab]);
  const [fy, setFy]               = useState("");          // empty = current FY
  const [branchId, setBranchId]   = useState("");
  const [division, setDivision]   = useState("");
  const [pStatus, setPStatus]     = useState("");
  const [search, setSearch]       = useState("");

  const [data, setData]           = useState(null);        // breach-alerts payload
  const [tmap, setTmap]           = useState(null);        // transfer-map payload
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState("");

  // Filter dropdown data
  const [branches, setBranches]   = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [fyList, setFyList]       = useState([]);

  // Load filter dropdowns once
  useEffect(() => {
    Promise.all([
      api.get("/branches"),
      api.get("/dashboard/divisions"),
      api.get("/financial-years"),
    ]).then(([b, d, f]) => {
      setBranches(b.data || []);
      setDivisions(d.data || []);
      const fys = (f.data || []).map((row) => row.fy_code || row.code).filter(Boolean);
      setFyList(fys);
      // Default to current FY if available — try the one with is_active.
      const active = (f.data || []).find((r) => r.is_active);
      if (active?.fy_code || active?.code) setFy(active.fy_code || active.code);
    }).catch(() => { /* swallow — dashboard works without dropdowns */ });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setErr("");
    const qs = new URLSearchParams();
    if (fy)       qs.set("fy_code", fy);
    if (branchId) qs.set("branch_id", branchId);
    if (division) qs.set("division", division);
    if (pStatus)  qs.set("project_status", pStatus);
    Promise.all([
      api.get(`/dashboard/budget-breach-alerts?${qs.toString()}`),
      api.get(`/dashboard/transfer-map?${qs.toString()}`),
    ])
      .then(([r1, r2]) => { setData(r1.data); setTmap(r2.data); })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load Project Health"))
      .finally(() => setLoading(false));
  }, [fy, branchId, division, pStatus]);

  useEffect(() => { load(); }, [load]);

  // Client-side text filter on top of server-filtered rows
  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) =>
      (r.project_name || "").toLowerCase().includes(q) ||
      (r.project_code || "").toLowerCase().includes(q) ||
      (r.client_name  || "").toLowerCase().includes(q) ||
      (r.branch_name  || "").toLowerCase().includes(q));
  }, [data, search]);

  return (
    <div className="space-y-6" data-testid="project-health-dashboard">
      <PageHeader
        eyebrow="Sprint 3 — Project Health"
        title="Project Health Dashboard"
        subtitle="Budget utilization, breach alerts & inter-project material flow — across every active project"
        actions={
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-2 text-xs uppercase tracking-wider font-bold text-slate-700 hover:border-[#F97316] hover:text-[#F97316]"
            data-testid="ph-refresh-btn"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        }
      />

      {/* Summary tiles */}
      <SummaryTiles counts={data?.counts} loading={loading} />

      {/* Filter bar */}
      <FilterBar
        fy={fy} setFy={setFy} fyList={fyList}
        branchId={branchId} setBranchId={setBranchId} branches={branches}
        division={division} setDivision={setDivision} divisions={divisions}
        pStatus={pStatus} setPStatus={setPStatus}
        search={search} setSearch={setSearch}
      />

      {err && (
        <div className="border border-rose-300 bg-rose-50 px-4 py-3 text-xs text-rose-800" data-testid="ph-error">
          {err}
        </div>
      )}

      {/* Project Health Grid */}
      <Card className="rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-700">
            <Activity size={14} /> Project Health Grid
            <span className="text-slate-400 normal-case font-normal tracking-normal">
              · {filteredRows.length} of {data?.total ?? 0}
            </span>
          </div>
        </div>
        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-slate-500" data-testid="ph-loading">Loading…</div>
        ) : filteredRows.length === 0 ? (
          <EmptyState title="No projects match these filters" hint="Try clearing the filters or selecting a different FY" testid="ph-empty" />
        ) : (
          <HealthGrid rows={filteredRows} openCostSummary={openCostSummary} />
        )}
      </Card>

      {/* Net Commitment & Transfer Map */}
      <TransferMap tmap={tmap} loading={loading} openCostSummary={openCostSummary} />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Summary tiles
// ─────────────────────────────────────────────────────────────────────
function SummaryTiles({ counts, loading }) {
  const total = STATUS_ORDER.reduce((s, k) => s + (counts?.[k] || 0), 0);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ph-summary-tiles">
      {STATUS_ORDER.map((s) => {
        const M = STATUS_META[s];
        const T = STATUS_TONES[M.tone];
        const Icon = M.icon;
        const n = counts?.[s] ?? 0;
        return (
          <div key={s}
               className={`px-4 py-4 ring-1 ${T.tile} rounded`}
               data-testid={`ph-tile-${s}`}>
            <div className="flex items-center justify-between mb-2">
              <Icon size={16} />
              <span className="font-display text-2xl font-black tabular-nums" data-testid={`ph-tile-${s}-count`}>
                {loading ? "…" : n}
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold">{M.label}</div>
            <div className="text-[10px] opacity-70 mt-0.5">{M.hint}</div>
          </div>
        );
      })}
      <div className="px-4 py-4 ring-1 ring-slate-200 bg-white rounded" data-testid="ph-tile-Total">
        <div className="flex items-center justify-between mb-2">
          <FileBarChart2 size={16} className="text-slate-500" />
          <span className="font-display text-2xl font-black tabular-nums text-slate-900" data-testid="ph-tile-Total-count">
            {loading ? "…" : total}
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Total Projects</div>
        <div className="text-[10px] text-slate-500 mt-0.5">In current filter scope</div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Filter bar
// ─────────────────────────────────────────────────────────────────────
function FilterBar({ fy, setFy, fyList,
                    branchId, setBranchId, branches,
                    division, setDivision, divisions,
                    pStatus, setPStatus,
                    search, setSearch }) {
  const select = "w-full border border-slate-300 bg-white px-3 py-2 text-xs focus:outline-none focus:border-[#F97316] transition-colors";
  const label  = "text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1.5";
  return (
    <Card className="rounded-xl">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-700">
        <Filter size={14} /> Filters
      </div>
      <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-5 gap-4">
        <div>
          <div className={label}>Financial Year</div>
          <select className={select} value={fy} onChange={(e) => setFy(e.target.value)} data-testid="ph-filter-fy">
            <option value="">— All FY —</option>
            {fyList.map((f) => <option key={f} value={f}>FY {f}</option>)}
          </select>
        </div>
        <div>
          <div className={label}>Branch</div>
          <select className={select} value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="ph-filter-branch">
            <option value="">— All Branches —</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
          </select>
        </div>
        <div>
          <div className={label}>Division</div>
          <select className={select} value={division} onChange={(e) => setDivision(e.target.value)} data-testid="ph-filter-division">
            <option value="">— All Divisions —</option>
            {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <div className={label}>Project Status</div>
          <select className={select} value={pStatus} onChange={(e) => setPStatus(e.target.value)} data-testid="ph-filter-status">
            <option value="">— All Statuses —</option>
            {PROJECT_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <div className={label}>Search</div>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Project / Client / Branch"
              className={`${select} pl-7`}
              data-testid="ph-filter-search"
            />
          </div>
        </div>
      </div>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Project Health Grid
// ─────────────────────────────────────────────────────────────────────
function HealthGrid({ rows, openCostSummary }) {
  // Fixed-height scroll segment — keeps the rest of the dashboard
  // (summary tiles, filter bar, Transfer Map) anchored while the user
  // pages through the project list. Sticky <thead> so column labels
  // never disappear mid-scroll.
  return (
    <div className="overflow-auto max-h-[60vh]" data-testid="ph-grid-scroll">
      <table className="w-full text-xs" data-testid="ph-grid-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.16em] text-slate-600 font-bold sticky top-0 z-10 shadow-[inset_0_-1px_0_0_rgb(226_232_240)]">
          <tr>
            <th className="px-3 py-2.5 text-left">Project</th>
            <th className="px-3 py-2.5 text-right">Budget</th>
            <th className="px-3 py-2.5 text-right">Consumed</th>
            <th className="px-3 py-2.5 text-right">Open Commit.</th>
            <th className="px-3 py-2.5 text-right">Revenue</th>
            <th className="px-3 py-2.5 text-right">Current Profit</th>
            <th className="px-3 py-2.5 text-right">Expected Profit</th>
            <th className="px-3 py-2.5 text-right">Util %</th>
            <th className="px-3 py-2.5 text-left">Status</th>
            <th className="px-3 py-2.5 text-right">{/* action */}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const M = STATUS_META[r.status] || STATUS_META.Not_Configured;
            const T = STATUS_TONES[M.tone];
            return (
              <tr key={r.project_id}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${T.row}`}
                  data-testid={`ph-row-${r.project_id}`}>
                <td className="px-3 py-2.5">
                  <div className="font-bold text-slate-900">{r.project_name}</div>
                  <div className="text-[10px] text-slate-500 tabular-nums">
                    {r.project_code}{r.branch_name ? ` · ${r.branch_name}` : ""}{r.division ? ` · ${r.division}` : ""}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {r.has_budget ? formatINR(r.budget) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatINR(r.consumed)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatINR(r.open_commitments)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatINR(r.revenue_billed)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${r.current_profit < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                  {r.current_profit >= 0 ? "+" : ""}{formatINR(r.current_profit)}
                </td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${r.expected_profit < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                  {r.expected_profit >= 0 ? "+" : ""}{formatINR(r.expected_profit)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {r.utilization_pct == null
                    ? <span className="text-slate-400">—</span>
                    : <span className={r.status === "Breach" ? "text-rose-700 font-bold" : ""}>
                        {r.utilization_pct.toFixed(1)}%
                      </span>}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${T.chip} rounded`}
                        data-testid={`ph-row-${r.project_id}-status`}>
                    {M.label}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline"
                    onClick={() => openCostSummary(r)}
                    data-testid={`ph-row-${r.project_id}-drilldown`}
                  >
                    Cost Summary <ExternalLink size={10} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Net Commitment & Transfer Map
// ─────────────────────────────────────────────────────────────────────
function TransferMap({ tmap, loading, openCostSummary }) {
  const totals = tmap?.totals || {};
  const edges  = tmap?.edges  || [];
  const nodes  = tmap?.nodes  || [];
  return (
    <Card className="rounded-xl overflow-hidden" data-testid="ph-transfer-map">
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-700">
          <ArrowRight size={14} /> Net Commitment &amp; Transfer Map
          <span className="text-slate-400 normal-case font-normal tracking-normal">
            · {totals.edges || 0} flow{totals.edges === 1 ? "" : "s"} across {totals.nodes || 0} project{totals.nodes === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
          <span>MTV <span className="text-slate-700 tabular-nums">{totals.mtv_transfers || 0}</span></span>
          <span>IV <span className="text-slate-700 tabular-nums">{totals.iv_transfers  || 0}</span></span>
          <span>Total Value <span className="text-slate-700 tabular-nums">{formatINR(totals.total_value || 0)}</span></span>
        </div>
      </div>

      {loading ? (
        <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="ph-map-loading">Loading…</div>
      ) : (edges.length === 0 && nodes.length === 0) ? (
        <EmptyState
          title="No inter-project material flow yet"
          hint="Approve a Material Transfer Voucher or a cross-project Issue Voucher to populate this map"
          testid="ph-map-empty"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-100">
          {/* Edges — flow list */}
          <div className="lg:col-span-2 bg-white">
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-600">Top Value Flows</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Sorted by absolute material value transferred</div>
            </div>
            <div className="max-h-[460px] overflow-y-auto" data-testid="ph-map-edges">
              {edges.slice(0, 25).map((e, idx) => (
                <div key={`${e.source_id}-${e.dest_id}`}
                     className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 hover:bg-slate-50"
                     data-testid={`ph-map-edge-${idx}`}>
                  <button
                    type="button"
                    onClick={() => openCostSummary({ project_id: e.source_id, project_name: e.source_name, project_code: "", branch_name: "" })}
                    className="flex-1 min-w-0 text-left hover:underline"
                    data-testid={`ph-map-edge-${idx}-source`}
                  >
                    <div className="font-bold text-slate-900 text-xs truncate">{e.source_name}</div>
                    <div className="text-[10px] text-slate-500">Source</div>
                  </button>
                  <div className="shrink-0 text-center">
                    <ArrowRight size={16} className="text-[#F97316] mx-auto" />
                    <div className="text-[10px] tabular-nums font-bold text-slate-900 mt-0.5">{formatINR(e.total_value)}</div>
                    <div className="text-[10px] text-slate-500">
                      {e.mtv_count > 0 && `${e.mtv_count} MTV`}
                      {e.mtv_count > 0 && e.iv_count > 0 && " · "}
                      {e.iv_count  > 0 && `${e.iv_count} IV`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openCostSummary({ project_id: e.dest_id, project_name: e.dest_name, project_code: "", branch_name: "" })}
                    className="flex-1 min-w-0 text-right hover:underline"
                    data-testid={`ph-map-edge-${idx}-dest`}
                  >
                    <div className="font-bold text-slate-900 text-xs truncate">{e.dest_name}</div>
                    <div className="text-[10px] text-slate-500">Destination</div>
                  </button>
                </div>
              ))}
              {edges.length > 25 && (
                <div className="px-5 py-3 text-center text-[10px] text-slate-500">
                  Showing top 25 of {edges.length} flows
                </div>
              )}
            </div>
          </div>

          {/* Nodes — per-project net flow */}
          <div className="bg-white">
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-600">Per-Project Net Flow</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Inbound minus Outbound material value</div>
            </div>
            <div className="max-h-[460px] overflow-y-auto" data-testid="ph-map-nodes">
              {nodes.map((n) => (
                <div key={n.project_id}
                     className="px-5 py-3 border-b border-slate-100 hover:bg-slate-50"
                     data-testid={`ph-map-node-${n.project_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 text-xs truncate">{n.project_name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{n.branch_name}{n.division ? ` · ${n.division}` : ""}</div>
                    </div>
                    <div className={`tabular-nums text-xs font-bold ${n.net_flow < 0 ? "text-rose-700" : n.net_flow > 0 ? "text-emerald-700" : "text-slate-500"}`}>
                      {n.net_flow >= 0 ? "+" : ""}{formatINR(n.net_flow)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500 tabular-nums">
                    <span>Out {formatINR(n.total_out)}</span>
                    <span className="text-slate-300">·</span>
                    <span>In {formatINR(n.total_in)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
